<template>
    <div v-if="comic != null" id="comic" class="py-sm-4">
        <div class="comic-hero card">
            <div class="comic-hero__body">
                <div class="comic-hero__cover">
                    <img
                        :src="comic.thumbnail"
                        class="rounded"
                        :alt="comic.title"
                        :title="comic.title"
                    />
                </div>
                <div class="comic-hero__content">
                    <div class="comic-hero__title-row">
                        <h1 class="comic-hero__title">{{ comic.title }}</h1>
                        <span
                            v-if="comic.status"
                            class="badge badge-status comic-hero__status"
                            title="Status"
                            :data-status="
                                comic.status ? comic.status.toLowerCase() : ''
                            "
                        >
                            {{ comic.status }}
                        </span>
                        <a
                            v-if="comic.id !== null"
                            class="comic-hero__edit"
                            :href="
                                reader.BASE_URL + 'admin/comics/' + comic.slug
                            "
                            target="_blank"
                            title="Редактировать"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path
                                    d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                ></path>
                                <path
                                    d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                ></path>
                            </svg>
                        </a>
                    </div>

                    <div
                        v-if="comic.alt_titles.length"
                        class="comic-hero__subtitle"
                    >
                        {{ comic.alt_titles.join(" • ") }}
                    </div>

                    <div class="comic-hero__meta">
                        <div
                            v-if="comic.author || comic.artist"
                            class="comic-hero__meta-line"
                        >
                            <span v-if="comic.author">
                                <span class="comic-hero__meta-label"
                                    >{{ reader.__("Author") }}:</span
                                >
                                {{ comic.author }}
                            </span>
                            <span
                                v-if="comic.author && comic.artist"
                                class="comic-hero__divider"
                                >•</span
                            >
                            <span v-if="comic.artist">
                                <span class="comic-hero__meta-label"
                                    >{{ reader.__("Artist") }}:</span
                                >
                                {{ comic.artist }}
                            </span>
                        </div>

                        <div
                            v-if="comic.target || comic.genres.length"
                            class="comic-hero__meta-line"
                        >
                            <span
                                v-if="comic.target"
                                class="comic-hero__meta-label"
                                >{{ reader.__("Target") }}:</span
                            >
                            <router-link
                                v-if="comic.target != null"
                                :to="'/targets/' + comic.target.toLowerCase()"
                                class="comic-hero__tag me-2"
                                >{{ comic.target }}
                            </router-link>
                            <span
                                v-if="comic.genres.length"
                                class="comic-hero__meta-label"
                                >{{ reader.__("Genres") }}:</span
                            >
                            <template v-for="genre in comic.genres">
                                <router-link
                                    :to="'/genres/' + genre.slug"
                                    class="comic-hero__tag me-1"
                                >
                                    {{ genre.name }}
                                </router-link>
                            </template>
                        </div>

                        <div class="comic-hero__meta-line">
                            <span class="comic-hero__meta-label"
                                >{{ reader.__("Format") }}:</span
                            >
                            <span class="me-2">{{
                                comic.format_name || "—"
                            }}</span>
                        </div>

                        <div
                            v-if="comic.last_chapter != null"
                            class="comic-hero__meta-line"
                        >
                            <span class="comic-hero__meta-label"
                                >{{ reader.__("Last chapter") }}:</span
                            >
                            <router-link
                                :to="comic.last_chapter.url"
                                class="text-success fw-bold"
                            >
                                {{
                                    formatChapterTitle(
                                        comic.last_chapter.full_title,
                                    )
                                }}
                            </router-link>
                        </div>

                        <div class="comic-hero__meta-line">
                            <span class="comic-hero__meta-label"
                                >{{ reader.__("Stats") }}:</span
                            >
                            <span class="me-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    style="
                                        color: #ffc107;
                                        vertical-align: -2px;
                                        margin-right: 4px;
                                    "
                                >
                                    <polygon
                                        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                    ></polygon>
                                </svg>
                                <span
                                    v-if="comic.rating != null"
                                    title="Рейтинг"
                                    >{{ comic.rating }}</span
                                >
                                <span v-else title="Рейтинг">-.--</span>
                            </span>
                            <span style="white-space: nowrap">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    style="
                                        color: var(--bs-info);
                                        vertical-align: -2px;
                                        margin-right: 4px;
                                    "
                                >
                                    <path
                                        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                    ></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                <span title="Просмотры">{{ comic.views }}</span>
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="comic.description"
                        class="comic-hero__description text-pre"
                    >
                        {{ comic.description }}
                    </div>
                </div>
            </div>
        </div>

        <div id="comic_top_html" class="text-center"></div>

        <div class="card mt-3">
            <div class="card-header">
                <div class="row">
                    <div class="col-sm-9 pt-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            style="margin-right: 8px; vertical-align: -3px"
                        >
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path
                                d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                            ></path>
                        </svg>
                        {{ reader.__("Chapters") }}
                    </div>
                    <div class="col-sm-3">
                        <input
                            class="form-control me-sm-2 ui-autocomplete-input card-search"
                            type="search"
                            :placeholder="reader.__('Filter chapters')"
                            aria-label="Filter"
                            name="filter"
                            autocomplete="off"
                        />
                    </div>
                </div>
            </div>
            <div class="card-body">
                <div
                    class="row border-bottom py-2 d-sm-flex d-none chapter-list-header"
                >
                    <div
                        class="col-sm-auto text-center pe-0"
                        style="width: 50px"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                    </div>
                    <div class="col text-start">
                        {{ reader.__("Title") }}
                    </div>
                    <div
                        class="col-sm-auto text-end ps-0"
                        style="width: 120px; white-space: nowrap"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            style="margin-right: 4px; vertical-align: -2px"
                        >
                            <path
                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                            ></path>
                            <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        {{ reader.__("Views") }}
                    </div>
                    <div class="col-sm-auto text-end ps-0" style="width: 140px">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            style="margin-right: 4px; vertical-align: -2px"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        Дата
                    </div>
                </div>

                <div
                    v-for="chapter in comic.chapters"
                    :class="
                        'row flex-sm-nowrap text-truncate border-bottom py-2 chapter-item item' +
                        (chapter.hidden ? ' hidden' : '') +
                        (chapter.licensed ? ' licensed' : '') +
                        (getViewed(chapter.slug_lang_vol_ch_sub) ? ' read' : '')
                    "
                >
                    <div
                        class="col-auto text-sm-center pe-0 order-0 overflow-hidden chapter-read-indicator"
                        style="width: 50px"
                    >
                        <span
                            :class="
                                'cursor-pointer chapter-check' +
                                (getViewed(chapter.slug_lang_vol_ch_sub)
                                    ? ' checked'
                                    : '')
                            "
                            :title="
                                getViewed(chapter.slug_lang_vol_ch_sub)
                                    ? 'Отметить как непрочитанное'
                                    : 'Отметить как прочитанное'
                            "
                            @click="setViewed(chapter.slug_lang_vol_ch_sub)"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </span>
                    </div>
                    <div class="col text-start order-1 text-truncate">
                        <span class="chapter-title">
                            <a
                                v-if="chapter.id !== null"
                                :href="
                                    reader.BASE_URL +
                                    'admin/comics/' +
                                    comic.slug +
                                    '/chapters/' +
                                    chapter.id
                                "
                                target="_blank"
                                class="chapter-edit-btn"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path
                                        d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                    ></path>
                                    <path
                                        d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                    ></path>
                                </svg>
                            </a>
                            <router-link
                                v-if="!chapter.licensed || chapter.id !== null"
                                :to="chapter.url"
                                class="filter chapter-link"
                                :title="chapter.full_title"
                                >{{
                                    formatChapterTitle(chapter.full_title)
                                }}</router-link
                            >
                            <span
                                v-else
                                data-bs-toggle="modal"
                                data-bs-target="#modal-container"
                                class="filter chapter-link"
                                :title="chapter.full_title + ' [LICENSED]'"
                                >{{
                                    formatChapterTitle(chapter.full_title)
                                }}</span
                            >
                        </span>
                    </div>
                    <div
                        class="col-auto text-end overflow-hidden order-2 ps-0 ch-views d-none d-sm-block"
                        style="width: 120px"
                    >
                        {{ chapter.views }}
                    </div>
                    <div
                        class="col-auto text-end overflow-hidden order-3 ps-0 ch-time"
                        :title="new Date(chapter.published_on)"
                        style="width: 140px"
                    >
                        {{ chapter.time }}
                    </div>
                </div>
            </div>
        </div>

        <div id="comic_bottom_html" class="text-center"></div>

        <popup :popupData="popupData"></popup>
    </div>
    <div v-else ref="notfound"></div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import NotFound from "./NotFound.vue";
import Popup from "./Popup.vue";

export default {
    name: "Comic",
    components: { NotFound, Popup },
    mounted() {
        $("body").removeClass("body-reader hide-header");
        $("#nav-search").show();
        $("#nav-filter").hide();

        this.$store.dispatch("fetchComic", this.$route).then(() => {
            if (this.$store.getters.comic === null) {
                const ComponentClass = Vue.extend(NotFound);
                const instance = new ComponentClass({
                    propsData: { reader: this.reader },
                });
                instance.$mount();
                if (typeof this.$refs.notfound !== "undefined")
                    this.$refs.notfound.appendChild(instance.$el);
                const title = "Error 404 - " + this.reader.SITE_NAME;
                $("title").html(title);
                $('meta[property="og:title"]').attr("content", title);
            } else {
                const comic = this.$store.getters.comic;
                const sName = this.reader.SITE_NAME || "MangaReader";
                const title = comic.title + " - Читать онлайн - " + sName;
                const desc =
                    "Читать " +
                    (comic.format_name || "мангу").toLowerCase() +
                    " " +
                    comic.title +
                    " онлайн бесплатно на русском языке." +
                    (comic.description
                        ? " " + comic.description.substring(0, 200)
                        : "");
                const url = this.reader.BASE_URL + comic.url.replace(/^\//, "");
                $("title").html(title);
                $('meta[property="og:title"]').attr("content", title);
                $('meta[name="description"]').attr("content", desc);
                $('meta[property="og:description"]').attr("content", desc);
                $('meta[name="twitter:description"]').attr("content", desc);
                $('meta[name="twitter:title"]').attr("content", title);
                $('link[rel="canonical"]').attr("href", url);
                $('meta[property="og:url"]').attr("content", url);
                if (comic.thumbnail) {
                    $('meta[property="og:image"]').attr(
                        "content",
                        comic.thumbnail,
                    );
                    $('meta[property="og:image:secure_url"]').attr(
                        "content",
                        comic.thumbnail,
                    );
                    $('meta[name="twitter:image"]').attr(
                        "content",
                        comic.thumbnail,
                    );
                }
            }
            filter_search();
        });
        this.comic != null && this.updateCustomHTML();
        $("html,body").scrollTop(0);
    },
    updated() {
        this.comic != null && this.updateCustomHTML();
    },
    data() {
        return {
            reader: this.$root,
            viewed: this.$root.getSetting("viewed")
                ? JSON.parse("" + this.$root.getSetting("viewed"))
                : {},
            popupData: {
                header: this.$root.__("Chapter licensed"),
                body: this.$root.__(
                    "This chapter is licensed and you can't read it.",
                ),
                button: "Ok",
            },
        };
    },
    methods: {
        formatChapterTitle(fullTitle) {
            if (!fullTitle) return "";
            return fullTitle.replace(/^Ch\.?\s*/i, "Глава ");
        },
        getViewed(slug) {
            if (
                typeof this.viewed[this.$store.getters.comic.slug] ===
                "undefined"
            ) {
                return 0;
            }
            return this.viewed[this.$store.getters.comic.slug][slug] || 0;
        },
        setViewed(slug) {
            if (
                typeof this.viewed[this.$store.getters.comic.slug] ===
                "undefined"
            ) {
                this.viewed[this.$store.getters.comic.slug] = {};
            }
            this.viewed[this.$store.getters.comic.slug][slug] ^= 1;
            localStorage.setItem("viewed", JSON.stringify(this.viewed));
            //this.reader.setCookie('viewed', JSON.stringify(this.viewed), 3650);
            this.$forceUpdate();
        },
        updateCustomHTML() {
            this.reader.updateCustomHTML("comic_top_html");
            this.reader.updateCustomHTML("comic_bottom_html");
        },
    },
    computed: {
        ...mapGetters(["comic"]),
    },
};
</script>
